import React, { useContext, useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import ReactPlayer from "react-player";
import Slider from "react-slick";
import youTubePlayButton from "../../assets/newImages/Play.svg";
import { Next, Previous } from "../../components/Icons/Icons";
import { GlobalContext } from "../../components/globalContext/GlobalContext";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import "./partnerSlider.scss";
import { getYouTubeVideoId } from "../../utils/helper";

export default function PartnerCarousel(props) {
  const { globalState } = useContext(GlobalContext);
  const slider = React.useRef(null);
  const [playingVideo, setPlayingVideo] = useState(null);
  const playerRefs = useRef({});
  const [alreadyReArranged, setAlreadyReArranged] = useState(false)
  const [carouselData, setCarouselData] = useState([]);
  // const moveLastItemToSecondPosition = (arr) => {
  //   if (arr.length < 2) return arr;
  //   const lastItem = arr[arr.length - 1];
  //   const restItems = arr.slice(0, arr.length - 1);
  //   return [restItems[0], lastItem, ...restItems.slice(1)];
  // };

  useEffect(() => {
    setCarouselData(props.carouselData);
  }, [props.carouselData, props]);

  useEffect(() => {
    const handleResize = () => {
      if (!slider.current) return;

      const width = window.innerWidth;
      if (width <= 730) {
        setAlreadyReArranged(true)
        // shift left by 1 when showing 2 slides
        slider.current.slickGoTo(1, true);
      }
      else if (width > 730) {
        if (alreadyReArranged) {
          setAlreadyReArranged(false)
          // shift right by 1 when showing 2 slides
          slider.current.slickGoTo(-1, true);
        }
        else {
          slider.current.slickGoTo(0, true);
        }
      }
      slider.current.innerSlider?.onWindowResized();
    };

    window.addEventListener("resize", handleResize);
    handleResize();

    return () => window.removeEventListener("resize", handleResize);
  }, [carouselData, alreadyReArranged]);

  const handlePlay = (id) => {
    // Pause all other videos safely
    Object.keys(playerRefs.current).forEach((videoId) => {
      if (videoId !== id && playerRefs.current[videoId]) {
        const internalPlayer =
          playerRefs.current[videoId].getInternalPlayer?.();
        if (internalPlayer && typeof internalPlayer.pauseVideo === "function") {
          internalPlayer.pauseVideo(); // Only call if it's a YouTube/Vimeo player
        } else if (typeof playerRefs.current[videoId].pause === "function") {
          playerRefs.current[videoId].pause(); // Fallback: use ReactPlayer's own pause
        }
      }
    });

    // Set the new playing video
    setPlayingVideo(id);
  };

  const handlePause = (id) => {
    if (playingVideo === id) {
      setPlayingVideo(null);
    }
  };

  const handleEnded = () => {
    setPlayingVideo(null);
  };

  const NextArrow = (props) => {
    const { className, style, onClick } = props;
    return (
      <span
        style={{ ...style }}
        className={className}
        onClick={onClick}
        role="button"
      >
        <Next />
      </span>
    );
  };
  const PrevArrow = (props) => {
    const { className, style, onClick } = props;
    return (
      <span
        style={{ ...style }}
        className={className}
        onClick={onClick}
        role="button"
      >
        <Previous />
      </span>
    );
  };

  const renderMedia = (mediaLink, index, type) => {
    if (
      mediaLink.mediaType === "SOCIAL_MEDIA" ||
      mediaLink.mediaType === "VIMEO_MEDIA"
    ) {
      return (
        <div
          style={{
            position: "relative",
            width: "100%",
            maxWidth: "640px",
            margin: "auto",
          }}
          key={mediaLink.id}
        >
          <ReactPlayer
            ref={(player) => (playerRefs.current[mediaLink.id] = player)}
            url={mediaLink.url}
            playing={playingVideo === mediaLink.id}
            light={
              type === "vimeo"
                ? `https://vumbnail.com/${mediaLink.url?.split("/")[4]}.jpg`
                : `https://img.youtube.com/vi/${getYouTubeVideoId(mediaLink?.url)}/hqdefault.jpg`
            }
            playIcon={
              <div
                onClick={() => handlePlay(mediaLink.id)}
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 2,
                  cursor: "pointer",
                }}
              >
                <img
                  style={{
                    aspectRatio: "16 / 9",
                  }}
                  className="play-button"
                  src={youTubePlayButton}
                  alt="play icon"
                />
              </div>
            }
            width="100%"
            height="100%"
            controls
            onPause={() => handlePause(mediaLink.id)}
            onEnded={handleEnded}
            onPlay={() => handlePlay(mediaLink.id)}
            style={{
              aspectRatio: "16 / 9",
            }}
          />
        </div>
      );
    }
    if (mediaLink.mediaType === "SYNTHESIA_MEDIA") {
      const rawUrl = mediaLink?.url;

      if (!rawUrl) return null;

      const uuidMatch = rawUrl.match(
        /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/,
      );

      if (!uuidMatch) return null;

      const videoId = uuidMatch[0];

      // CORRECT embed URL
      const embedUrl = `https://share.synthesia.io/embeds/videos/${videoId}`;

      return (
        <div
          className="synthesia-video-container"
          style={{
            position: "relative",
            width: "100%",
            maxWidth: "600px",
            margin: "0 auto",
            paddingBottom: "56.25%", // 16:9
            height: 0,
          }}
        >
          <iframe
            src={embedUrl}
            title={mediaLink?.title || "Synthesia Video"}
            allow="autoplay; encrypted-media"
            allowFullScreen
            frameBorder="0"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              border: "none",
            }}
          ></iframe>
        </div>
      );
    }

    if (mediaLink.mediaType === "IMAGE") {
      return (
        <img
          src={mediaLink.url}
          alt="carousel item"
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          key={mediaLink.id}
        />
      );
    }
    return null;
  };

  const settings = {
    infinite: true,
    slidesToShow:
      window.innerWidth >= 2560
        ? carouselData?.length > 4
          ? 4
          : carouselData?.length
        : carouselData?.length > 3
          ? 3
          : carouselData?.length,
    slideToScroll: 3,
    autoplay: false,
    dots: false,
    swipeToSlide: true,
    swipe: true,
    speed: 500,
    // autoplay: true,
    autoplaySpeed: 2000,
    arrows: false,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    className: "slider variable-width",
    responsive: [
      {
        //  below 450px show single card
        breakpoint: 450,
        settings: {
          centerMode: true,
          slidesToShow: 1,
          slidesToScroll: 1,
          infinite: true,
          dots: false,
          speed: 500,
        },
      },
      {
        breakpoint: 730,
        settings: {
          centerMode: false,
          slidesToShow: carouselData?.length > 2 ? 2 : 1,
          slidesToScroll: 1,
          infinite: true,
          dots: false,
          speed: 500,
        },
      },
    ],
  };

  useEffect(() => {
    let playPauseBtnList = {};
    props?.carouselData?.forEach((element) => {
      playPauseBtnList[element?.id] = false;
    });
  }, [props.carouselData]);

  return (
    <motion.div
      className="h-100"
      initial={{ opacity: 0, scale: 0.5 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{
        duration: 0.8,
        delay: 0.5,
        ease: [0, 0.71, 0.2, 1.01],
      }}
    >
      {carouselData?.length > 0 ? (
        <div
          className={`partner-slider-container  ${carouselData?.length === 2 ? "slider-container-length-two" : ""}`}
        >
          {!globalState.isMobile &&
            carouselData &&
            carouselData?.length > 3 && (
              <PrevArrow
                key={"previousArrow"}
                onClick={() => {
                  slider?.current?.slickPrev();
                  setPlayingVideo("");
                }}
                className="cursor-pointer previous-arrow"
              />
            )}

          <Slider ref={slider} {...settings}>
            {carouselData &&
              carouselData.map((data, i) => {
                if (
                  data?.mediaLink?.mediaType === "SOCIAL_MEDIA" &&
                  data.mediaLink.url
                ) {
                  return (
                    <React.Fragment key={`${data.id}-SOCIAL_MEDIA`}>
                      <div className="slide ">
                        <div
                          style={{
                            width: "100%",
                            height: "500px",
                            overflow: "hidden",
                          }}
                        >
                          {renderMedia(data?.mediaLink, i, "youtube")}
                        </div>
                        <span className="slide-title-hidden">{data.title}</span>
                      </div>
                      <span className="slide-title">
                        {data.titleLink ? (
                          <a
                            href={data.titleLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="slide-title-link"
                          >
                            {data.title}
                          </a>
                        ) : (
                          data.title
                        )}
                      </span>

                    </React.Fragment>
                  );
                } else if (
                  data?.mediaLink?.mediaType === "VIMEO_MEDIA" &&
                  data.mediaLink.url
                ) {
                  return (
                    <React.Fragment key={`${data.id}-VIMEO_MEDIA`}>
                      <div className="slide">
                        {renderMedia(data?.mediaLink, i, "vimeo")}
                        <span className="slide-title-hidden">{data.title}</span>
                      </div>
                      <span className="slide-title">
                        {data.titleLink ? (
                          <a
                            href={data.titleLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="slide-title-link"
                          >
                            {data.title}
                          </a>
                        ) : (
                          data.title
                        )}
                      </span>

                    </React.Fragment>
                  );
                } else if (
                  data?.mediaLink?.mediaType === "SYNTHESIA_MEDIA" &&
                  data.mediaLink.url
                ) {
                  return (
                    <React.Fragment key={`${data.id}-SYNTHESIA_MEDIA`}>
                      <div className="slide ">
                        <div
                          style={{
                            width: "100%",
                            height: "500px",
                            overflow: "hidden",
                          }}
                        >
                          {renderMedia(data?.mediaLink, i, "synthesia")}
                        </div>
                        <span className="slide-title-hidden">{data.title}</span>
                      </div>
                      <span className="slide-title">
                        {data.titleLink ? (
                          <a
                            href={data.titleLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="slide-title-link"
                          >
                            {data.title}
                          </a>
                        ) : (
                          data.title
                        )}
                      </span>

                    </React.Fragment>
                  );
                } else {
                  return (
                    <React.Fragment key={`${data?.id}-image`}>
                      <div className="slide ">
                        <img
                          src={data?.mediaLink?.url}
                          alt={data?.title}
                          className="carousel-image "
                        />
                        <span className="slide-title-hidden">{data?.title}</span>
                      </div>
                      <span className="slide-title">
                        {data?.titleLink ? (
                          <a
                            href={data?.titleLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="slide-title-link"
                          >
                            {data?.title}
                          </a>
                        ) : (
                          data?.title
                        )}
                      </span>

                    </React.Fragment>
                  );
                }
              })}
          </Slider>
          {/* </div> */}
          {!globalState.isMobile &&
            carouselData &&
            carouselData?.length > 3 && (
              <NextArrow
                key={"nextArrow"}
                onClick={() => {
                  slider?.current?.slickNext();
                  setPlayingVideo("");
                }}
                className="cursor-pointer next-arrow"
              />
            )}
        </div>
      ) : (
        <div className="empty-partner" style={{ height: "100px" }}>
          No partner items available at the moment.
        </div>
      )}
    </motion.div>
  );
}
